import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Container from '../components/layout/Container';
import { useAuth } from '../hooks/useAuth';
import './LoginPage.css';

/**
 * LoginPage
 * User authentication page
 */
const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [challengeSession, setChallengeSession] = useState(null);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validate password change if required
      if (requiresPasswordChange) {
        if (!newPassword) {
          setError('Please enter a new password');
          setLoading(false);
          return;
        }
        if (newPassword !== confirmPassword) {
          setError('The passwords you entered do not match. Please try again.');
          setLoading(false);
          return;
        }
        if (newPassword.length < 8) {
          setError('Password must be at least 8 characters long');
          setLoading(false);
          return;
        }
        if (!/[A-Z]/.test(newPassword)) {
          setError('Password must contain at least one uppercase letter');
          setLoading(false);
          return;
        }
        if (!/[a-z]/.test(newPassword)) {
          setError('Password must contain at least one lowercase letter');
          setLoading(false);
          return;
        }
        if (!/[0-9]/.test(newPassword)) {
          setError('Password must contain at least one number');
          setLoading(false);
          return;
        }
        if (newPassword === password) {
          setError('New password must be different from your current password');
          setLoading(false);
          return;
        }
      }

      // Call login from AuthContext
      const response = await login(
        email, 
        password, 
        requiresPasswordChange ? newPassword : undefined, 
        requiresPasswordChange ? challengeSession : undefined
      );
      
      // Check if password change is required
      if (response.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setChallengeSession(response.session);
        setRequiresPasswordChange(true);
        setLoading(false);
        return;
      }

      // Successful login - redirect based on user role
      if (response.user?.role === 'Organizer' || response.user?.role === 'organizer') {
        navigate('/organizer/dashboard');
      } else if (response.user?.role === 'Captain' || response.user?.role === 'captain') {
        navigate('/captain/dashboard');
      } else {
        navigate('/');
      }
    } catch (err) {
      // Provide detailed error messages based on the error
      let errorMessage = 'An unexpected error occurred. Please try again.';
      
      if (err.message) {
        const msg = err.message.toLowerCase();
        
        if (msg.includes('incorrect') || msg.includes('password')) {
          errorMessage = 'The email or password you entered is incorrect. Please check and try again.';
        } else if (msg.includes('not found') || msg.includes('user')) {
          errorMessage = 'No account found with this email address. Please check your email or contact an administrator.';
        } else if (msg.includes('network') || msg.includes('fetch')) {
          errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
        } else if (msg.includes('required')) {
          errorMessage = 'Please fill in all required fields.';
        } else if (msg.includes('email') && msg.includes('verified')) {
          errorMessage = 'Your email address has not been verified. Please check your email for a verification link.';
        } else if (msg.includes('password') && msg.includes('change')) {
          errorMessage = 'Failed to change password. Please ensure your new password meets all requirements.';
        } else {
          // Use the original error message if it doesn't match any pattern
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <Container>
      <div className="login-page">
        <div className="login-card">
          <h1>Pool Tournament Manager</h1>
          <h2>{requiresPasswordChange ? 'Change Password' : 'Sign In'}</h2>

          {requiresPasswordChange && (
            <div className="info-message">
              Your temporary password needs to be changed. Please enter a new password.
            </div>
          )}

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            {!requiresPasswordChange ? (
              <>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    disabled={loading}
                    autoComplete="current-password"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="form-group">
                  <label htmlFor="newPassword">New Password</label>
                  <input
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    disabled={loading}
                    autoComplete="new-password"
                  />
                  <small>Minimum 8 characters, must include uppercase, lowercase, and numbers</small>
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    required
                    disabled={loading}
                    autoComplete="new-password"
                  />
                </div>
              </>
            )}

            <button
              type="submit"
              className="login-button"
              disabled={loading}
            >
              {loading ? (requiresPasswordChange ? 'Changing Password...' : 'Signing in...') : (requiresPasswordChange ? 'Change Password' : 'Sign In')}
            </button>
          </form>

          <div className="login-footer">
            <Link to="/" className="back-link">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default LoginPage;
